import styles from './OrderStatusProgressBar.module.css';
import Text, { TEXT_VARIANT } from '@pixum/text';

export interface OrderStatusProgressBarProps {
  status: string;
  constants: Record<string, string>;
}

/**
 * OrderStatusProgressBar component - Matches Gatsby design exactly
 * Server Component - displays horizontal progress bar with three stages
 *
 * Displays order progress through three stages:
 * 1. INIT - Order received
 * 2. PROCESSING - Order in production
 * 3. DELIVERED - Order shipped/delivered (includes SHIPPED, DELIVERY_*)
 *
 * Features:
 * - Horizontal bar design matching Gatsby implementation
 * - Border elements for track background
 * - Segment fills for progress indication
 * - Pulsing animation on active stage
 * - Text labels above SVG
 */
export function OrderStatusProgressBar({ status, constants }: OrderStatusProgressBarProps) {
  // Determine bar type based on status (matches Gatsby logic)
  const barType =
    status === 'SHIPPED' ||
    status === 'DELIVERED' ||
    status === 'DELIVERY_DATA_TRANSMITTED' ||
    status === 'DELIVERY_TRANSIT' ||
    status === 'DELIVERY_START' ||
    status === 'DELIVERY_OUT_FOR_DELIVERY' ||
    status === 'DELIVERY_DELAY' ||
    status === 'DELIVERY_UNDELIVERABLE' ||
    status === 'DELIVERY_PICKUP' ||
    status === 'RESHIPPED'
      ? 'DELIVERED'
      : status === 'SUBMITTED'
        ? 'INIT'
        : status;

  const orderInitLabel = constants['i18n_order-status_status_init'];
  const orderProcessingLabel = constants['i18n_order-status_status_processing'];
  const orderShippedLabel = constants['i18n_order-status_status_shipping'];

  return (
    <div className={`${styles.progressBar} ${styles[`progressBar--${barType}`]}`}>
      <Text variant={TEXT_VARIANT.CAPTION} className={styles.label}>
        {orderInitLabel}
      </Text>
      <Text variant={TEXT_VARIANT.CAPTION} className={styles.label}>
        {orderProcessingLabel}
      </Text>
      <Text variant={TEXT_VARIANT.CAPTION} className={styles.label}>
        {orderShippedLabel}
      </Text>

      <svg fill="none" height="42" width="268" xmlns="http://www.w3.org/2000/svg">
        {/* Border elements - gray background track */}
        <rect
          className={styles.barBorder}
          height="106"
          stroke="null"
          transform="rotate(-90 78.8458 21)"
          width="14"
          x="72"
          y="-32"
        />
        <circle className={styles.barBorder} cx="20.7" cy="21" r="14" transform="rotate(-90 20.6666 21)" />
        <circle className={styles.barBorder} cx="133.7" cy="21" r="14" transform="rotate(-90 133.667 21)" />
        <rect
          className={styles.barBorder}
          height="112"
          stroke="null"
          transform="rotate(-90 188.564 21)"
          width="14"
          x="181.6"
          y="-35"
        />
        <circle className={styles.barBorder} cx="247.7" cy="21" r="14" transform="rotate(-90 247.667 21)" />

        {/* Segment 2 - fills when DELIVERED */}
        <rect
          height="109"
          id="svg_segment2"
          stroke="null"
          transform="rotate(-90 190.231 21)"
          width="10"
          x="185"
          y="-33.4"
        />

        {/* Segment 1 - fills when PROCESSING or DELIVERED */}
        <rect
          height="106"
          id="svg_segment1"
          stroke="null"
          transform="rotate(-90 78.5894 21)"
          width="10"
          x="73.6"
          y="-32"
        />

        {/* Pulse circles - animate based on status */}
        <circle className={styles.pulse1} cx="20.5" cy="21" r="12" />
        <circle className={styles.pulse1} cx="20.5" cy="21" r="12" />

        {/* Circle 1 - INIT */}
        <circle cx="20.5" cy="21" id="svg_circle1" r="12" />

        {/* Pulse circles for circle 2 */}
        <circle className={styles.pulse2} cx="134" cy="21" r="12" />
        <circle className={styles.pulse2} cx="134" cy="21" r="12" />

        {/* Circle 2 - PROCESSING */}
        <circle cx="133.7" cy="21" id="svg_circle2" r="12" />

        {/* Pulse circles for circle 3 */}
        <circle className={styles.pulse3} cx="247" cy="21" r="12" />
        <circle className={styles.pulse3} cx="247" cy="21" r="12" />

        {/* Circle 3 - DELIVERED */}
        <circle cx="247.7" cy="21" id="svg_circle3" r="12" />
      </svg>
    </div>
  );
}
